import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route support-audit timestamp through clock.
import { clock } from '@/utils/clock';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { redactPII } from '@/utils/scrubBugContext';
import {
  reportSupportAuditFailure,
  __resetSupportAuditFailureWarnings,
} from '@/lib/support/supportAuditFailure';

/**
 * CORE-BJ (HARD-002): the audit insert used to be awaited WITHOUT reading its `{ error }` — a
 * PostgREST refusal (RLS, constraint, network) resolves rather than throws, so the catch never
 * ran and a support session could act in a customer's shop with no audit row and no signal.
 * Every write now checks its result; a failure is logged and the support operator is told once
 * per support session, so they know the session is not being recorded.
 */
// Core-100 R4: the one warn-once mechanism now lives in `@/lib/support/supportAuditFailure`,
// shared with the session brackets in SupportModeContext.
const reportAuditFailure = reportSupportAuditFailure;

/** Test seam: forget which sessions were already warned. */
export function __resetSupportAuditWarnings() {
  __resetSupportAuditFailureWarnings();
}

/**
 * Core-100 R2 CORE-BJ: support audit rows are written by the SERVER (`record_support_audit`),
 * which checks the session is the caller's and active and takes the actor and shop from the
 * session itself; a direct client insert claiming support context is refused by the database.
 */
async function recordSupportAudit(
  sessionId: string,
  row: { action: string; objectType: string; objectId: string; details: Record<string, unknown> },
): Promise<unknown | null> {
  try {
    const { error } = await supabase.rpc('record_support_audit' as never, {
      p_session_id: sessionId,
      p_action: row.action,
      p_object_type: row.objectType,
      p_object_id: row.objectId,
      p_details: row.details,
    } as never);
    return error ?? null;
  } catch (thrown) {
    return thrown ?? new Error('support audit threw');
  }
}

/**
 * Hook to log significant actions during support mode
 * Automatically includes support context when active
 * @returns `ok: false` when the row was not written (the operator has been told).
 */
export function useSupportAudit() {
  const { isInSupportMode, supportSession } = useSupportMode();

  const logAction = async (params: {
    action: string;
    objectType: string;
    objectId: string;
    details?: Record<string, any>;
  }): Promise<{ ok: boolean }> => {
    if (!isInSupportMode || !supportSession) return { ok: true };

    // IV-D-23 R2 FIX-1 (real-actor doctrine) is now enforced by the server: record_support_audit
    // writes auth.uid() — the operator who started the session — never the impersonated target.
    const error = await recordSupportAudit(supportSession.sessionId, {
      action: params.action,
      objectType: params.objectType,
      objectId: params.objectId,
      details: { ...params.details },
    });
    if (error) {
      reportAuditFailure(supportSession.sessionId, `support action ${params.action}`, error);
      return { ok: false };
    }
    return { ok: true };
  };

  return { logAction, isInSupportMode };
}

/**
 * Component that tracks page navigation during support mode
 */
export function SupportModeNavigationTracker() {
  const location = useLocation();
  const { isInSupportMode, supportSession } = useSupportMode();
  const { user } = useAuth();

  useEffect(() => {
    if (!isInSupportMode || !supportSession) return;

    const logPageView = async () => {
      const error = await recordSupportAudit(supportSession.sessionId, {
        action: 'support_page_view',
        objectType: 'navigation',
        objectId: supportSession.sessionId,
        details: {
          path: location.pathname,
          // A query string can carry a customer's phone, email or VIN; the audit row needs the
          // route, not the customer's data.
          search: redactPII(location.search),
          timestamp: clock.now().toISOString(),
        },
      });
      if (error) reportAuditFailure(supportSession.sessionId, 'support page view', error);
    };

    logPageView();
  }, [location.pathname, location.search, isInSupportMode, supportSession, user?.id]);

  return null;
}
